import { useMemo, useState } from 'react'
import { KpiCard, KpiStrip } from '../../../cc'
import { ReportTable } from '../../../cc/ReportTable'
import { BarChart, ProportionBar } from '../../charts/Charts'
import { useReportTableState } from '../../useReportTableState'
import { toApiParams } from '../alertFilters'
import { fmtInt, kindLabel, pct, SHIFT_LABEL } from '../alertLabels'
import type { ByShiftRow } from '../alertTypes'
import { useAlertAnalytics } from '../useAlertAnalytics'
import { AlertDrillList, type DrillScope } from './AlertDrillList'
import { asRows, columnDefs, makeExport, useViewExport, ViewCard, ViewState, type ViewProps } from './viewKit'

const COLUMNS = ['shift', 'kind', 'alerts', 'critical', 'vehicles', 'drivers', 'share_pct'] as const

/** Sun and moon: amber for day, indigo for night. Neither is a status colour, on purpose --
 *  night is not "bad", it is a different operating regime. */
export const DAY_COLOR = '#F59E0B'
export const NIGHT_COLOR = '#6366F1'

/** Day (06:00-22:00) vs night (22:00-06:00) IST, per alert kind. The question this
 *  answers is "is the night a different fleet?" -- fatigue-shaped kinds (harsh brake,
 *  overspeed on empty roads) rising after 22:00 is the signal. */
export function DayNightView({ filters, setFilters }: ViewProps): JSX.Element {
  const params = useMemo(() => toApiParams(filters), [filters])
  const { rows, status, error } = useAlertAnalytics<ByShiftRow>('/by-shift', params)

  const model = useMemo(() => {
    const day = rows.filter((r) => r.shift === 'day')
    const night = rows.filter((r) => r.shift === 'night')
    const sum = (rs: ByShiftRow[], k: 'alerts' | 'critical') => rs.reduce((s, r) => s + r[k], 0)
    const dayAlerts = sum(day, 'alerts')
    const nightAlerts = sum(night, 'alerts')
    const byKind = new Map<string, { day: number; night: number }>()
    for (const r of rows) {
      const cur = byKind.get(r.kind) ?? { day: 0, night: 0 }
      cur[r.shift] += r.alerts
      byKind.set(r.kind, cur)
    }
    const kinds = [...byKind.entries()]
      .sort((a, b) => (b[1].day + b[1].night) - (a[1].day + a[1].night))
      .slice(0, 12)
    // The kind whose night share most exceeds the fleet's own night share -- "what gets
    // worse after dark", not merely "what is biggest".
    const fleetNightShare = dayAlerts + nightAlerts > 0 ? nightAlerts / (dayAlerts + nightAlerts) : 0
    let nightHeavy: { kind: string; share: number } | null = null
    for (const [kind, v] of byKind) {
      const total = v.day + v.night
      if (total < 5) continue
      const share = v.night / total
      if (share > fleetNightShare && (!nightHeavy || share > nightHeavy.share)) nightHeavy = { kind, share }
    }
    return {
      dayAlerts, nightAlerts, dayCritical: sum(day, 'critical'), nightCritical: sum(night, 'critical'),
      kinds, nightHeavy, fleetNightShare,
    }
  }, [rows])

  // A shift x kind row clicked in the table below narrows the alert list under it.
  const [pick, setPick] = useState<{ shift: 'day' | 'night'; kind: string } | null>(null)
  const scope: DrillScope | null = useMemo(() => (pick ? {
    label: `${pick.shift === 'night' ? 'Night' : 'Day'} · ${kindLabel(pick.kind)}`,
    params: { shift: pick.shift, kind: pick.kind },
    onClear: () => setPick(null),
  } : null), [pick])

  const table = useReportTableState('alerts-by-shift', asRows(rows))
  const defs = useMemo(() => columnDefs(COLUMNS, {
    shift: {
      render: (v) => (
        <span className="inline-flex items-center gap-1.5 text-[12px] text-slate-700">
          <span className="h-2 w-2 rounded-full" style={{ background: v === 'night' ? NIGHT_COLOR : DAY_COLOR }} />
          {v === 'night' ? 'Night' : 'Day'}
        </span>
      ),
    },
  }), [])
  const exportFn = useMemo(
    () => makeExport(rows, [...COLUMNS], 'alerts-day-night', 'Alerts by shift', filters),
    [rows, filters],
  )
  useViewExport(exportFn)

  const totalAlerts = model.dayAlerts + model.nightAlerts

  return (
    <ViewState
      status={status} error={error} rowCount={rows.length} filters={filters} setFilters={setFilters}
      noData={{ title: 'No alerts in this window', body: 'Nothing fired for this site between these dates, by day or by night.' }}
    >
      <div className="space-y-4">
        <KpiStrip>
          <KpiCard label="Day alerts" value={model.dayAlerts} sub={SHIFT_LABEL.day} accentColor={DAY_COLOR} tone="neutral" />
          <KpiCard label="Night alerts" value={model.nightAlerts} sub={SHIFT_LABEL.night} accentColor={NIGHT_COLOR} tone="neutral" />
          <KpiCard
            label="Night share"
            value={pct(model.nightAlerts, totalAlerts)}
            unit="%"
            sub="night is 8 of 24 hours (33%)"
            tone={pct(model.nightAlerts, totalAlerts) > 40 ? 'warn' : 'neutral'}
          />
          <KpiCard
            label="Night critical"
            value={model.nightCritical}
            tone={model.nightCritical > 0 ? 'danger' : 'neutral'}
            dot={model.nightCritical > 0}
            sub={`${fmtInt(model.dayCritical)} by day`}
          />
          <KpiCard
            label="Worse after dark"
            value={model.nightHeavy ? kindLabel(model.nightHeavy.kind) : '—'}
            sub={model.nightHeavy ? `${Math.round(model.nightHeavy.share * 100)}% of it happens at night` : 'no kind skews to night'}
            tone={model.nightHeavy ? 'warn' : 'neutral'}
          />
        </KpiStrip>

        <ViewCard title="Day vs night" sub="Share of every alert in the window. Night is a third of the clock; more than a third of the alerts means the dark is harder.">
          <div role="img" aria-label={`Day ${fmtInt(model.dayAlerts)} alerts, night ${fmtInt(model.nightAlerts)} alerts`}>
            <ProportionBar
              height={12}
              segments={[
                { label: 'Day', value: model.dayAlerts, color: DAY_COLOR },
                { label: 'Night', value: model.nightAlerts, color: NIGHT_COLOR },
              ]}
            />
          </div>
        </ViewCard>

        <ViewCard title="By alert type" sub="Top 12 kinds, day beside night. Equal bars mean the kind does not care about the clock.">
          <div role="img" aria-label="Alerts per kind, day versus night">
            <BarChart
              height={160}
              data={model.kinds.map(([kind, v]) => ({ label: kindLabel(kind), values: { day: v.day, night: v.night }, title: `${kindLabel(kind)}: ${v.day} day / ${v.night} night` }))}
              series={[
                { key: 'day', label: 'Day', color: DAY_COLOR },
                { key: 'night', label: 'Night', color: NIGHT_COLOR },
              ]}
            />
          </div>
        </ViewCard>

        <ViewCard title="Every shift × kind" sub="Share is within the shift, so day and night each sum to 100%. Click a row to list its alerts below." bodyClassName="p-0">
          <ReportTable
            columns={[...COLUMNS]}
            rows={table.sorted}
            columnDefs={defs}
            sort={table.sort}
            onSort={table.toggleSort}
            onRowClick={(row) => {
              const next = { shift: row.shift === 'night' ? 'night' as const : 'day' as const, kind: String(row.kind) }
              setPick((cur) => (cur && cur.shift === next.shift && cur.kind === next.kind ? null : next))
            }}
            compact
            applyLoadStateLabels={false}
            className="rounded-none ring-0 shadow-none"
          />
        </ViewCard>

        <AlertDrillList
          filters={filters}
          setFilters={setFilters}
          scope={scope}
          idleHint="Click a shift × kind row above to see just those alerts."
        />
      </div>
    </ViewState>
  )
}
